import React, { useEffect, useMemo, useState } from "react";
import Button from "@common/components/Button";
import Typography from "@common/components/Typography";
import { CloudUpload } from "@mui/icons-material";
import {
  Box,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  LinearProgress,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  Stack,
} from "@mui/material";
import Dropzone, { FileError } from "react-dropzone";
import { UploadType } from "..";
import { getKBSize } from "@common/utils/translation";
import { useTranslation } from "react-i18next";
import {
  IImportMode,
  ImportMode,
} from "@pages/Assets/common/ImportListContext";
import MenuItem from "@common/components/MenuItem";
import { useUserSelectedAccounts } from "@services/account/hooks/useUserSelectedAccounts";
import { useAssetNavigationParams } from "@pages/Assets/common/const";
import Select from "@common/components/Select";
import { Account } from "@services/account/types";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";

interface UploadDialogContentProps {
  acceptedFiles?: { [key: string]: string[] };
  uploadType: UploadType;
  validator?: (file: File) => FileError | null;
  progress: number;
  showProgressBar: boolean;
  dispatch: Function;
  errorFile?: string;
  fileSize?: string;
  fileName?: string;
  importMode?: IImportMode;
  setImportMode?: (mode: IImportMode) => void;
  mustHaveAccount?: boolean;
  fileFormatInformation?: string;
  targetAccountId?: string;
  setTargetAccountId?: (accountId: string) => void;
  additionalText?: string;
}

const UploadDialogContent = (props: UploadDialogContentProps) => {
  const {
    acceptedFiles = {},
    uploadType,
    validator = () => {
      return null;
    },
    progress,
    showProgressBar,
    dispatch,
    errorFile,
    fileSize,
    fileName,
    importMode = ImportMode.EXCEL,
    setImportMode = () => {},
    mustHaveAccount,
    targetAccountId,
    setTargetAccountId = () => {},
    fileFormatInformation,
    additionalText,
  } = props;

  const { t } = useTranslation();

  const [accounts, , allAccounts] = useUserSelectedAccounts();
  const [accountMenuItems, setAccountMenuItems] = useState<Account[]>();
  const [user] = useAuthenticatedUser();
  const data = useMemo<Account[]>(() => {
    return allAccounts.filter((acc) => accounts.indexOf(acc.id ?? "") !== -1);
  }, [allAccounts, accounts]);

  const handleFileSelect = (files: File[]) => {
    let name = "";
    let size = "";
    if (files) {
      name = files[0].name.replace(/\\/g, "/").replace(/.*\//, "").trim();
      size = getKBSize(files[0].size, t);
      dispatch({ selectedFile: files[0], errorFile: "" });
    }
    if (size != "") size = "(" + size + ")";
    dispatch({ fileName: name, fileSize: size });
  };

  const getTypeFile = () => {
    let filesTypesArray: string[] = [];
    Object.keys(acceptedFiles).forEach((key) => {
      filesTypesArray = filesTypesArray.concat(acceptedFiles[key]);
    });

    return filesTypesArray.join(", ").replace(/, ([^,]*)$/, " or $1");
  };
  const { fromAccount: accountId } =
    useAssetNavigationParams().navigationParams;
  const { watchList: watchList } = useAssetNavigationParams().navigationParams;

  const buildAccountPicker = () => {
    let tmp = [];
    if (
      user?.role.rolePermissions.adminAccountsVisibility == "HomeAccountOnly"
    ) {
      tmp.push(user.account);
    } else {
      tmp = data;
    }
    setAccountMenuItems(tmp);
  };

  const onAccountChange = (event: SelectChangeEvent<any>) => {
    setTargetAccountId(event.target.value);
  };

  useEffect(() => {
    buildAccountPicker();
  }, [user, data]);

  return (
    <DialogContent>
      {uploadType === UploadType.CSV_IMPORT && (
        <FormControl>
          <RadioGroup
            defaultValue={ImportMode.EXCEL}
            row
            onChange={(e) => {
              setImportMode(e.target.value as IImportMode);
            }}
          >
            <FormControlLabel
              value={ImportMode.EXCEL}
              control={<Radio />}
              label={t("Upload New")}
            />
            <FormControlLabel
              value={ImportMode.BULK_UPLOAD}
              control={<Radio />}
              label={t("Update Existing")}
            />
            <FormControlLabel
              value={ImportMode.MANUAL}
              control={<Radio />}
              label={t("Create Manually")}
            />
          </RadioGroup>
        </FormControl>
      )}
      {importMode !== ImportMode.MANUAL && (
        <>
          <Dropzone
            noClick
            noKeyboard
            maxFiles={1}
            multiple={false}
            accept={acceptedFiles}
            onDrop={(acceptedFiles, fileRejections) => {
              if (acceptedFiles.length > 0) handleFileSelect(acceptedFiles);
              if (fileRejections.length > 0)
                dispatch({
                  errorFile: t("You can only upload {{types}} file.", {
                    types: getTypeFile(),
                  }),
                });
            }}
            validator={(t) => {
              return validator(t);
            }}
            disabled={showProgressBar}
          >
            {({
              isDragActive,
              isDragReject,
              getRootProps,
              getInputProps,
              open: openDrop,
            }) => (
              <Stack
                {...getRootProps()}
                sx={{
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                  mb: 1,
                  width: "100%",
                  border: "2px dashed white",
                  backgroundColor: ({ palette }) =>
                    isDragReject
                      ? palette.error.light
                      : isDragActive
                      ? palette.success.main
                      : "none",
                }}
              >
                <input {...getInputProps()} data-testid="file-input" />
                <CloudUpload fontSize="large" sx={{ scale: "2", my: 2 }} />
                <Typography size="big">{t("Drag & drop")}</Typography>
                <Typography size="big">{t("OR")}</Typography>
                <Button
                  onClick={openDrop}
                  color="primary"
                  variant="outlined"
                  sx={{ my: 2 }}
                  disabled={showProgressBar}
                  data-testid="browse-file-button"
                >
                  {"Browse File"}
                </Button>
                <Stack direction={"column"} sx={{ alignItems: "center" }}>
                  <Typography size="smaller">
                    {t("Please, provide a {{types}} file.", {
                      types: getTypeFile(),
                    })}
                  </Typography>
                  {additionalText && (
                    <Typography
                      sx={{ marginTop: (theme) => theme.spacing(0.5) }}
                    >
                      {additionalText}
                    </Typography>
                  )}
                </Stack>

                {fileFormatInformation && (
                  <Typography size="smaller">
                    {t(fileFormatInformation)}
                  </Typography>
                )}
              </Stack>
            )}
          </Dropzone>
          <FormHelperText>{errorFile}</FormHelperText>
          <Typography>
            {fileName} {fileSize}
          </Typography>
          {showProgressBar && (
            <Box
              sx={{
                mt: 2,
              }}
            >
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          )}
        </>
      )}
      {mustHaveAccount && (
        <FormControl fullWidth sx={{ pt: 1 }}>
          <Select
            required
            value={targetAccountId ?? ""}
            name="account"
            fullWidth
            onChange={onAccountChange}
            label={t("Account")}
          >
            {accountMenuItems?.map((account) => (
              <MenuItem key={account.id} value={account.id}>
                {account.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </DialogContent>
  );
};
export default UploadDialogContent;
